import { FeedbackProvider, ToastProvider } from '@ibe/ui';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { AuthProvider } from './auth/auth-context.js';
import { type ApiClient } from './lib/api/client.js';
import { ApiProvider } from './lib/api/api-context.js';
import { createQueryClient } from './lib/api/query-client.js';

import { AppRoutes } from './routes.js';

export interface AppProps {
  readonly client: ApiClient;
  /** Injected by tests so each one gets an empty cache. */
  readonly queryClient?: QueryClient;
}

/**
 * The provider stack, in the order the layers depend on each other.
 *
 * The API client is outermost because auth needs it; auth sits outside the
 * router because a route guard reads the session; the query client is outside
 * the routes because pages query.
 *
 * The Zustand store needs no provider, which is part of why it is a store:
 * one fewer layer here, and it is readable outside React.
 */
export function App({ client, queryClient }: AppProps): React.JSX.Element {
  // Built once. A new client per render would drop the cache on every
  // keystroke that re-rendered this component.
  const resolvedQueryClient = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);

  return (
    <ApiProvider client={client}>
      <QueryClientProvider client={resolvedQueryClient}>
        <AuthProvider client={client}>
          <ToastProvider>
            <FeedbackProvider>
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </FeedbackProvider>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ApiProvider>
  );
}
